import {
  Box,
  Flex,
  Link,
  VStack,
  useColorModeValue,
  Icon,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Image,
  Text,
  Badge,
} from '@chakra-ui/react'
import React, { useState, useMemo } from 'react'
import { CitedReference, getCitedReferences } from '../../core/parsers/markdownParser'
import { Reference } from '../../types/chatTypes'
import { ExternalLink, ChevronDown, ChevronUp } from 'lucide-react'
import { getPageNumber, toWebUrl } from '../../utils/util'

interface ReferencesDisplayProps {
  references?: Reference[]
  // The answer body — used to show only the sources actually cited inline,
  // numbered to match their `[n]` markers.
  answerText?: string
}

const getDomain = (url: string): string => {
  try {
    const urlObj = new URL(toWebUrl(url))
    let domain = urlObj.hostname.replace(/^www\./, '')
    domain = domain.replace(/\.(com|org|net|edu|gov|co\.uk|co|io|ai|app)$/, '')
    return domain
  } catch {
    return url
  }
}

const getFavicon = (url: string): string => {
  try {
    const urlObj = new URL(toWebUrl(url))
    const fullDomain = urlObj.hostname.replace(/^www\./, '')
    return `https://icons.duckduckgo.com/ip3/${fullDomain}.ico`
  } catch {
    return `https://icons.duckduckgo.com/ip3/${url}.ico`
  }
}

const SourcePreview: React.FC<{ reference: CitedReference }> = ({ reference }) => {
  const [faviconError, setFaviconError] = useState(false)
  const domain = getDomain(reference.url)
  const pageNumber = reference.page || getPageNumber(reference.url)
  const grayLight = useColorModeValue('gray.50', 'gray.800')
  const grayBorder = useColorModeValue('gray.200', 'gray.600')
  const grayHover = useColorModeValue('gray.100', 'gray.700')
  const grayHoverBorder = useColorModeValue('gray.300', 'gray.500')
  const faviconColor = useColorModeValue('#718096', '#cbd5e0')
  const titleColor = useColorModeValue('gray.900', 'gray.50')
  const domainColor = useColorModeValue('gray.500', 'gray.400')
  const numberBg = useColorModeValue('gray.200', 'gray.600')
  const numberColor = useColorModeValue('gray.600', 'gray.200')

  return (
    <Link href={reference.url} isExternal _hover={{ textDecoration: 'none' }}>
      <Flex
        px={2}
        py={1.5}
        bg={grayLight}
        borderRadius='md'
        border={`1px solid ${grayBorder}`}
        _hover={{
          bg: grayHover,
          borderColor: grayHoverBorder,
        }}
        transition='all 0.2s'
        align='center'
        gap={2}
        cursor='pointer'
      >
        {/* Citation number — matches the inline [n] marker */}
        <Box
          display='flex'
          alignItems='center'
          justifyContent='center'
          minW='18px'
          h='18px'
          px={1}
          borderRadius='sm'
          bg={numberBg}
          color={numberColor}
          fontSize='10px'
          fontWeight='bold'
          flexShrink={0}
        >
          {reference.number}
        </Box>

        {/* Favicon */}
        {!faviconError ? (
          <Image
            src={getFavicon(reference.url)}
            alt=''
            w={4}
            h={4}
            borderRadius='sm'
            flexShrink={0}
            fallback={<ExternalLink size={16} color={faviconColor} />}
            onError={() => setFaviconError(true)}
          />
        ) : (
          <ExternalLink size={16} color={faviconColor} style={{ flexShrink: 0 }} />
        )}

        {/* Content */}
        <Box flex={1} minWidth={0}>
          <Text fontSize='sm' fontWeight={600} color={titleColor} noOfLines={1}>
            {reference.title}
          </Text>
          <Flex align='center' gap={1.5}>
            <Text fontSize='xs' color={domainColor} noOfLines={1}>
              {domain}
            </Text>
            {pageNumber && (
              <Badge size='sm' colorScheme='blue' fontSize='9px' flexShrink={0}>
                p. {pageNumber}
              </Badge>
            )}
          </Flex>
        </Box>
      </Flex>
    </Link>
  )
}

const ReferencesDisplay: React.FC<ReferencesDisplayProps> = ({ references, answerText }) => {
  const [isOpen, setIsOpen] = useState(false)

  // All hooks must be called at top level
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const badgeBgColor = useColorModeValue('gray.100', 'gray.700')
  const badgeHoverColor = useColorModeValue('gray.200', 'gray.600')
  const badgeCircleBg = useColorModeValue('gray.300', 'gray.500')
  const badgeCircleText = useColorModeValue('white', 'gray.100')
  const textColor = useColorModeValue('gray.700', 'gray.200')
  const popoverBg = useColorModeValue('white', 'gray.800')

  // Only the sources actually cited inline, numbered to match their markers.
  const citedReferences = useMemo(
    () => getCitedReferences(answerText, references),
    [answerText, references],
  )

  if (citedReferences.length === 0) {
    return null
  }

  return (
    <Popover isOpen={isOpen} onClose={() => setIsOpen(false)} placement='top'>
      <PopoverTrigger>
        <Box
          as='button'
          onClick={() => setIsOpen(!isOpen)}
          cursor='pointer'
          _focus={{ outline: 'none' }}
          width='fit-content'
        >
          <Flex
            align='center'
            gap={1.5}
            px={2.5}
            py={1.5}
            bg={badgeBgColor}
            borderRadius='full'
            border={`1px solid ${borderColor}`}
            _hover={{ bg: badgeHoverColor }}
            transition='all 0.2s'
            fontSize='xs'
            fontWeight={500}
            color={textColor}
          >
            <Box
              display='flex'
              alignItems='center'
              justifyContent='center'
              width='16px'
              height='16px'
              borderRadius='full'
              bg={badgeCircleBg}
              color={badgeCircleText}
              fontSize='10px'
              fontWeight='bold'
            >
              {citedReferences.length}
            </Box>
            <Box>{citedReferences.length === 1 ? 'source' : 'sources'}</Box>
            <Icon as={isOpen ? ChevronUp : ChevronDown} boxSize={3} ml={0.5} transition='transform 0.2s' />
          </Flex>
        </Box>
      </PopoverTrigger>

      <PopoverContent
        bg={popoverBg}
        border={`1px solid ${borderColor}`}
        borderRadius='lg'
        boxShadow='lg'
        p={0}
        w='auto'
        maxW='420px'
      >
        <PopoverBody p={3}>
          <VStack align='stretch' spacing={2}>
            {/* Header */}
            <Box px={1} pb={2} borderBottom={`1px solid ${borderColor}`}>
              <Text fontSize='sm' fontWeight={600} color={textColor}>
                {citedReferences.length} {citedReferences.length === 1 ? 'Source' : 'Sources'}
              </Text>
            </Box>

            {/* Sources List */}
            <VStack align='stretch' spacing={1.5} maxH='400px' overflowY='auto'>
              {citedReferences.map(reference => (
                <SourcePreview key={reference.number} reference={reference} />
              ))}
            </VStack>
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

export default ReferencesDisplay
